import React from 'react'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import {
    HeaderContainer,
    StyledDivider,
    StyledNightlyEditionButtonContainerLeft,
    StyledNightlyEditionContentHome,
    StyledNightlyEditionPictureRight,
    StyledNightlyEditionTextContainerLeft,
} from './SpecialEditionHome.styled'
import { HeaderPrefix } from '../SpecialEdition/SpecialEdition.styled'
import { scaleImage, WebLink } from '@news-mono/web-common'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import { breakpointMax, breakpoint } from '../../../__styling'

interface TheNightlyOnHomeProps {
    title: string
    teaser: string
    link: string
    imageURL: string
    lazyloadImages?: boolean
}

/**
 * @deprecated - This component uses a hard-coded list of "The Nightly On" editions
 * `apps/thenightly/src/app/routes/thenightly-on/onEditionsConfig.ts`
 * to render the latest edition on the homepage CTA
 * This component is to be removed once "The Nightly On" editions are published via CUE and replaced with the
 * `SpecialEditionHome` component which allows for more dynamic application.
 */
export const TheNightlyOnHome = ({
    title,
    teaser,
    link,
    imageURL,
    lazyloadImages,
}: TheNightlyOnHomeProps) => {
    return (
        <>
            <StyledDivider />
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        <HeaderPrefix>The Nightly On</HeaderPrefix>
                        <StyledNightlyEditionHeading>
                            {title} Edition
                        </StyledNightlyEditionHeading>
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            In this Edition
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <NightlyButton
                            variant={'default'}
                            text={`${title} Edition`}
                            action={{
                                type: 'link',
                                to: link,
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        <NightlyButton
                            variant={'default'}
                            text={'More Articles'}
                            action={{
                                type: 'link',
                                to: `/on/${title
                                    .toLowerCase()
                                    .replace(' ', '-')}`,
                            }}
                            color={'primary'}
                            fill={'outline'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionPictureRight>
                        <WebLink to={link}>
                            <img
                                loading={lazyloadImages ? 'lazy' : undefined}
                                src={imageURL}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = require('../../ePaper/assets/paper.jpg')
                                }}
                                alt={'The Nightly Special Edition'}
                            />
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}

export interface SpecialEditionHomeProps {
    edition: EditionV4DTO
    publicationTitle: string
    lazyloadImages?: boolean
}

export const SpecialEditionHome = ({
    edition,
    publicationTitle,
    lazyloadImages,
}: SpecialEditionHomeProps) => {
    const mainImage = edition.mainImages && {
        img: scaleImage(edition.mainImages[0].src, 600, false),
        alt: edition.mainImages[0].altText ?? edition.mainImages[0].captionText,
    }

    // The title for "The Nightly On" editions is in the format "title_DD-MM-YYYY", this
    // removes the date part and normalizes the title to be capitalized.
    const normalizedEditionTitle = edition.title
        ? edition.title.split('_')[0].charAt(0).toUpperCase() +
          edition.title.split('_')[0].slice(1).toLowerCase()
        : ''

    return (
        <>
            <StyledDivider />
            <StyledNightlyEditionContainer style={{ paddingBottom: 0 }}>
                <StyledNightlyEditionContentHome>
                    <HeaderContainer>
                        <HeaderPrefix>{publicationTitle}</HeaderPrefix>
                        <StyledNightlyEditionHeading>
                            {normalizedEditionTitle} Edition
                        </StyledNightlyEditionHeading>
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainerLeft>
                        <StyledNightlyEditionSubHeading>
                            In this Edition
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {edition.teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainerLeft>
                    <StyledNightlyEditionButtonContainerLeft>
                        <NightlyButton
                            variant={'default'}
                            text={`${normalizedEditionTitle} Edition`}
                            action={{
                                type: 'link',
                                to: edition.editionWebUrl,
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        <NightlyButton
                            variant={'default'}
                            text={'More Articles'}
                            action={{
                                type: 'link',
                                to: edition.primaryTopic
                                    ? `${edition.primaryTopic}`
                                    : '/on',
                            }}
                            color={'primary'}
                            fill={'outline'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                    </StyledNightlyEditionButtonContainerLeft>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionPictureRight>
                        <WebLink to={edition.editionWebUrl}>
                            {mainImage && (
                                <picture>
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpointMax('sm', false)}
                                    />
                                    <source
                                        srcSet={mainImage.img}
                                        media={breakpoint('sm', false)}
                                    />
                                    <img
                                        src={mainImage.img}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                        }}
                                        alt={mainImage.alt}
                                        loading={
                                            lazyloadImages ? 'lazy' : undefined
                                        }
                                    />
                                </picture>
                            )}
                        </WebLink>
                    </StyledNightlyEditionPictureRight>
                </StyledNightlyEditionContentHome>
            </StyledNightlyEditionContainer>
        </>
    )
}
